import Vue from 'vue'
import ptiMixin from '@/utils/pti/ptiMixin'
import {
  INACTIVITY_DETECTION_CANCEL,
  INACTIVITY_DETECTION_SPEED_WALK,
  INACTIVITY_NOTIFICATION_PREVENT_MESSAGE,
  INACTIVITY_NOTIFICATION_PREVENT_TITLE,
  INACTIVITY_NOTIFICATION_SEND_CAPTION,
  INACTIVITY_NOTIFICATION_SEND_MESSAGE,
  INACTIVITY_STATUS_ACTIVE,
  INACTIVITY_STATUS_DESKTOP,
  INACTIVITY_STATUS_INACTIVE,
  INACTIVITY_STATUS_INACTIVITY,
  INACTIVITY_STATUS_LIST,
  INACTIVITY_STATUS_WALK,
  INACTIVITY_WALK_DETECTION_DATE_DIFF
} from '@/consts/pti/inactivityDetector'
import { $ui } from '@/utils/ui'
import store from '@/store'
import { Notify } from 'quasar'
import { moveToForeground } from '@/utils/navigator/backgroundTask'
import { SERVICE_POST_EVENT_PTI } from '@/store/modules/service/action-types'
import { TYPE_AGENT_INACTIVE } from '@/consts/events'
import { checkIfWalking } from '@/utils/pti/utils'

const { cordova } = window
const NOTIFICATION_ID = 3

export default new Vue({
  name: 'InactivityDetector',
  eventSettingsCheckKey: 'ptiCheckInactivity',
  statusList: INACTIVITY_STATUS_LIST,
  mixins: [ptiMixin],
  data () {
    return {
      isWalking: false,
      walkLastTick: null,
      inactivityGlobalInterval: null,
      inactivityGlobalTimeout: null,
      inactivityLoopInterval: null,
      inactivityNotification: false,
      closeNotify: _.noop
    }
  },
  computed: {
    ptiFrequency () {
      return process.env.DEV
        ? 10
        : Math.max(5, _.get(store.getters['user/company'], ['agencySettingsFeature', 'ptiFrequency'], 30))
    },
    detectionTickrate () {
      return process.env.DEV
        ? Math.floor(this.ptiFrequency * 1500)
        : Math.floor(this.ptiFrequency * 60 * 1000)
    }
  },
  watch: {
    status: {
      immediate: true,
      async handler (status) {
        switch (status) {
          case INACTIVITY_STATUS_INACTIVE.value: {
            this._clearInactivityGlobalTimers()
            this._handleWindowEvent('devicemotion', 'onDeviceMotion', false)
            break
          }
          case INACTIVITY_STATUS_ACTIVE.value: {
            this._handleWindowEvent('devicemotion', 'onDeviceMotion')
            this._clearInactivityGlobalTimers()
            this.isWalking = false

            this.status = INACTIVITY_STATUS_INACTIVITY.value
            break
          }
          case INACTIVITY_STATUS_INACTIVITY.value: {
            this._clearInactivityGlobalInterval()

            this.inactivityGlobalInterval = setInterval(() => {
              if (this.isWalking) {
                this._clearInactivityGlobalInterval()
                return
              }

              this._clearInactivityLoopInterval()
              this.inactivityLoop()
            }, this.detectionTickrate + (INACTIVITY_DETECTION_CANCEL * 1000))
            break
          }
          case INACTIVITY_STATUS_WALK.value: {
            this.isWalking = true
            this._clearInactivityGlobalTimers()
            this._handleWindowEvent('devicemotion', 'onDeviceMotion')
            break
          }
          default:
            break
        }
      }
    }
  },
  beforeDestroy () {
    this._clearInactivityGlobalTimers()
  },
  methods: {
    _onActiveStateChange () {
      this.reset()
    },
    inactivityLoop (maxCountInactivity = INACTIVITY_DETECTION_CANCEL) {
      const tickInterval = 3000
      this.closeNotify = Notify.create({
        message: INACTIVITY_NOTIFICATION_PREVENT_TITLE,
        caption: INACTIVITY_NOTIFICATION_PREVENT_MESSAGE,
        timeout: INACTIVITY_DETECTION_CANCEL * tickInterval,
        position: 'center',
        progress: true,
        color: 'negative',
        multiLine: true,
        actions: [
          {
            label: 'R.A.S',
            color: 'white',
            handler: () => this._clearInactivitySubTimers()
          }
        ]
      })

      this.inactivityLoopInterval = setInterval(async () => {
        if (cordova) {
          cordova.plugins.notification.local.schedule({
            id: NOTIFICATION_ID,
            color: '#FF0000',
            sound: null,
            title: maxCountInactivity === 0
              ? 'Inactivité signalée'
              : `Signalement d'inactivité ${maxCountInactivity}.`,
            text: `${INACTIVITY_NOTIFICATION_PREVENT_TITLE}\n${INACTIVITY_NOTIFICATION_PREVENT_MESSAGE}`,
            progressBar: { value: (maxCountInactivity / INACTIVITY_DETECTION_CANCEL) * 100 }
          })
          this.inactivityNotification = true
        }

        if (maxCountInactivity > 0) {
          this.alarmPlay({ loop: false })
          --maxCountInactivity
          return
        }

        this._clearInactivitySubTimers()
        await store.dispatch('service/' + SERVICE_POST_EVENT_PTI, {
          type: store.getters.constServiceEventTypesKeys[TYPE_AGENT_INACTIVE.typeKey]
        })

        if (cordova) moveToForeground()
        Notify.create({
          message: INACTIVITY_NOTIFICATION_SEND_MESSAGE,
          caption: INACTIVITY_NOTIFICATION_SEND_CAPTION,
          color: 'negative',
          timeout: 0,
          multiLine: true,
          actions: [{
            label: 'Fermer',
            color: 'white'
          }]
        })
      }, tickInterval)
    },
    _handleWindowEvent (eventName, methodName, status = true) {
      window.removeEventListener(eventName, this[methodName], true)
      if (status) {
        window.addEventListener(eventName, this[methodName], true)
      }
    },
    _clearInactivityGlobalTimeout () {
      if (!this.inactivityGlobalTimeout) {
        return
      }

      clearTimeout(this.inactivityGlobalTimeout)
      this.inactivityGlobalTimeout = null
    },
    _clearInactivityGlobalInterval () {
      if (!this.inactivityGlobalInterval) {
        return
      }

      clearInterval(this.inactivityGlobalInterval)
      this.inactivityGlobalInterval = null
    },
    _clearInactivityLoopInterval () {
      if (cordova && this.inactivityNotification) {
        cordova.plugins.notification.local.clear([NOTIFICATION_ID])
        this.inactivityNotification = false
      }
      if (this.closeNotify) this.closeNotify()
      if (!this.inactivityLoopInterval) {
        return
      }

      clearInterval(this.inactivityLoopInterval)
      this.inactivityLoopInterval = null
    },
    _clearInactivitySubTimers () {
      this._clearInactivityLoopInterval()
      this._clearInactivityGlobalTimeout()
    },
    _clearInactivityGlobalTimers () {
      this._clearInactivityLoopInterval()
      this._clearInactivityGlobalTimeout()
      this._clearInactivityGlobalInterval()
    },
    reset () {
      const { isActive } = this
      this._clearInactivityGlobalTimers()
      this.walkLastTick = null

      if (!(this.$q.platform.is.mobile || $ui.isDev)) {
        this.status = INACTIVITY_STATUS_DESKTOP.value
        return
      }

      this.status = isActive
        ? INACTIVITY_STATUS_ACTIVE.value
        : INACTIVITY_STATUS_INACTIVE.value
    },
    onDeviceMotion ({ acceleration }) {
      switch (this.status) {
        case INACTIVITY_STATUS_ACTIVE.value:
        case INACTIVITY_STATUS_INACTIVITY.value: {
          if (checkIfWalking(acceleration.x, acceleration.y, INACTIVITY_DETECTION_SPEED_WALK)) {
            this.walkLastTick = null
            this.status = INACTIVITY_STATUS_WALK.value
          }
          break
        }
        case INACTIVITY_STATUS_WALK.value: {
          if (!checkIfWalking(acceleration.x, acceleration.y, INACTIVITY_DETECTION_SPEED_WALK)) {
            if (!this.walkLastTick) {
              this.walkLastTick = new Date().getTime()
            } else if ((new Date().getTime()) - this.walkLastTick > INACTIVITY_WALK_DETECTION_DATE_DIFF) {
              this.status = INACTIVITY_STATUS_ACTIVE.value
            }
          } else if ((new Date().getTime()) - this.walkLastTick <= INACTIVITY_WALK_DETECTION_DATE_DIFF) {
            this.walkLastTick = null
          }
          break
        }
        default:
          break
      }
    }
  }
})
